/**
 * ConnectingDots — sparse, slow-drifting particle network rendered on a
 * canvas. White/grey on black. Lines appear between nearby dots and fade
 * with distance, giving a calm, premium "technology / mesh" feel.
 *
 * The canvas is rendered absolutely positioned at full size of its parent.
 * Parent should be `relative`.
 */
import { useEffect, useRef } from "react";

export default function ConnectingDots({
  density = 0.00006,   // particles per pixel² — sparse
  linkDist = 160,      // px — how close 2 dots must be to draw a link
  speed = 0.18,        // px/frame — slow drift
  dotColor = "rgba(255,255,255,0.55)",
  lineColor = "255,255,255",
  className = "",
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let width = 0;
    let height = 0;

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // rebuild particles when size changes meaningfully
      const target = Math.max(28, Math.floor(width * height * density));
      const arr = particlesRef.current;
      if (arr.length !== target) {
        particlesRef.current = new Array(target).fill(null).map(() => ({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * speed,
          vy: (Math.random() - 0.5) * speed,
          r: Math.random() * 1.2 + 0.6,
        }));
      }
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement);

    const tick = () => {
      const ps = particlesRef.current;
      ctx.clearRect(0, 0, width, height);

      // move
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        if (p.y < -10) p.y = height + 10;
        if (p.y > height + 10) p.y = -10;
      }

      // links
      const ld2 = linkDist * linkDist;
      for (let i = 0; i < ps.length; i++) {
        for (let j = i + 1; j < ps.length; j++) {
          const a = ps[i];
          const b = ps[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < ld2) {
            const alpha = (1 - d2 / ld2) * 0.35;
            ctx.strokeStyle = `rgba(${lineColor},${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // dots
      ctx.fillStyle = dotColor;
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [density, linkDist, speed, dotColor, lineColor]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none ${className}`}
      data-testid="connecting-dots-canvas"
      aria-hidden="true"
    />
  );
}
